/*
 * ECSR-820: keep the PDP "Use It With" carousel as tall as the card it is showing.
 *
 * The carousel runs with Flickity's adaptiveHeight (set in the block's
 * data-slider-options, snippets/main-product-blocks.liquid), which writes the
 * selected cell's measured height onto .flickity-viewport as an inline style.
 * Every cell is position: absolute, so that inline height is the only thing
 * giving the viewport a size — and it is measured once, when Flickity
 * initialises.
 *
 * The one-card-per-view layout made that single measurement unreliable. The
 * card's height is now driven by how many lines the product name takes, and the
 * name is set in an Adobe Fonts webfont ("neue-haas-grotesk-display") that
 * commonly resolves after Flickity has already measured: at a 1280px viewport
 * the card grew from 122px to 145px on the font swap and the last ~23px of it —
 * the "Add to Bag - $price" link — was clipped by the stale viewport. Anything
 * else that changes the card's height after init (a late product image, a
 * translated label) lands the same way.
 *
 * So: re-measure whenever the cell's height actually changes, rather than
 * trusting the one measurement at init. <flickity-component>.update() is the
 * component's own "re-measure" entry point (it calls the Flickity instance's
 * resize()), and it only ever writes the viewport height — the observed cell is
 * absolutely positioned with an auto height, so a refresh cannot change what the
 * observer is watching and the two cannot chase each other.
 *
 * The block is rendered by <product-recommendations>, which fetches its markup
 * and writes it in with innerHTML, so this cannot be a script tag beside the
 * carousel (innerHTML does not run scripts) and the component does not exist at
 * DOMContentLoaded on a PDP that loads its recommendations asynchronously. Hence
 * the MutationObserver on the section.
 */
(function () {
  var SECTION = '.f-complementary-products';
  var CELL = '.f-complementary-products__slide';

  function flickityOf(component) {
    return component.slider && component.slider.instance;
  }

  /*
   * component.update() re-measures through Flickity's resize(), which
   * repositions every cell and re-selects the current one. Doing that while the
   * shopper has a finger down on the carousel fights the drag they are in the
   * middle of, and doing it mid-animation can land the selection on a cell the
   * carousel is already moving away from — the "out of sync" class of bug. So a
   * refresh that arrives during an interaction is deferred to the next settle
   * rather than dropped: the height still needs correcting, just not mid-drag.
   */
  function refresh(component) {
    if (typeof component.update !== 'function') {
      return;
    }
    var flickity = flickityOf(component);
    var busy = flickity && (flickity.isPointerDown || flickity.isAnimating);

    if (!busy) {
      component.update();
      syncArrowSpacing(component);
      return;
    }
    if (component.dataset.uiwRefreshQueued === 'on') {
      return;
    }
    component.dataset.uiwRefreshQueued = 'on';
    flickity.once('settle', function () {
      component.dataset.uiwRefreshQueued = '';
      component.update();
      syncArrowSpacing(component);
    });
  }

  /*
   * Tell the arrows how wide the dots row actually is.
   *
   * The bottom-centred arrows are positioned off --actual-pagination-width.
   * dynamic-arrow-positioning.js is what normally measures that, but it runs
   * setupObservers() once at DOMContentLoaded over the carousels already in the
   * page — and this one is not one of them: <product-recommendations> fetches
   * and injects it later, and only once the shopper scrolls near it. So it is
   * never measured, the variable keeps the --pagination-width: 2rem fallback
   * that main-product-blocks.liquid sets inline, and the arrows get placed for
   * a 21.6px dots row that is really 87px — landing them on top of the dots.
   * The wider the row (more products), the worse the overlap.
   *
   * Measured here from the dots' own tap targets rather than the visible dots,
   * because the targets are what a thumb actually hits, and converted with the
   * document's real root font size — this theme's is 10.8px, so the shared
   * script's hardcoded /16 would under-report the row by a third even if it
   * did run. Scoped to this component: nothing else's arrows are touched.
   */
  function syncArrowSpacing(component) {
    var wrapper = component.closest('.flickity-wrapper') || component;
    var dots = component.querySelectorAll('.flickity-page-dots .dot');
    if (!dots.length) {
      return;
    }
    var first = dots[0].getBoundingClientRect();
    var last = dots[dots.length - 1].getBoundingClientRect();
    /* ::after is the tap target and does not affect the element's own box, so
       add the overhang on each side back in. */
    var target = parseFloat(getComputedStyle(dots[0], '::after').width) || first.width;
    var span = (last.right - first.left) + Math.max(0, target - first.width);
    var rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    /* Deliberately NOT --actual-pagination-width: that one is read by the
       shared arrow CSS at every width, so writing it here would move the
       arrows on desktop too, where nothing was wrong. These two are private to
       the mobile rules in custom-product.css. */
    wrapper.style.setProperty('--uiw-dots-span', (span / rootPx) + 'rem');

    /* The arrows' own percentage resolves against a different box than the dots
       row does, so give them the dots' actual centre, measured relative to the
       element the buttons are positioned against. */
    var wrapRect = wrapper.getBoundingClientRect();
    var center = ((first.left + last.right) / 2) - wrapRect.left;
    wrapper.style.setProperty('--uiw-dots-center', center + 'px');
  }

  function observe(component) {
    if (component.dataset.uiwHeightSync === 'on') {
      return;
    }
    component.dataset.uiwHeightSync = 'on';
    syncArrowSpacing(component);

    var cell = component.querySelector(CELL);

    /* No ResizeObserver (or no cell to watch): fall back to the single event
       that causes this in practice, the webfont resolving. */
    if (!cell || typeof ResizeObserver !== 'function') {
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () {
          refresh(component);
        });
      }
      return;
    }

    var lastHeight = 0;
    var observer = new ResizeObserver(function (entries) {
      /*
       * <product-recommendations> replaces this whole subtree with innerHTML
       * when it swaps its fetched markup in, which detaches the cell without
       * ever telling the observer. Without this the observer — and through its
       * closure the old component and cell — stays alive for the life of the
       * page, and a second swap would leave two of them re-measuring carousels
       * that are no longer on the page.
       */
      if (!cell.isConnected) {
        observer.disconnect();
        return;
      }
      var height = entries[0].contentRect.height;
      /* ResizeObserver fires once on observe(), which is the initial sync. */
      if (Math.abs(height - lastHeight) < 1) {
        return;
      }
      lastHeight = height;
      refresh(component);
    });
    observer.observe(cell);
  }

  var scanQueued = false;

  function scan() {
    scanQueued = false;
    var components = document.querySelectorAll(SECTION + ' flickity-component');
    for (var i = 0; i < components.length; i++) {
      observe(components[i]);
    }
  }

  /* Flickity itself mutates the carousel constantly (it repositions cells on
     every select), so coalesce the observer's callbacks into one pass a frame. */
  function queueScan() {
    if (scanQueued) {
      return;
    }
    scanQueued = true;
    requestAnimationFrame(scan);
  }

  function start() {
    scan();
    var sections = document.querySelectorAll(SECTION);
    for (var i = 0; i < sections.length; i++) {
      new MutationObserver(queueScan).observe(sections[i], {
        childList: true,
        subtree: true,
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
